import { useApiQuery } from '../useApiQuery';
import { useHasPermission } from '@/hooks/usePermissions';
import { apiGet, operationsClient } from '@/lib/api';
import { jobEndpoints } from '@/lib/api/endpoints';
import { jobKeys } from '@/lib/queryKeys';
import type { JobListResponse } from '@/types/job';
import { useCompanyTimezone } from '@/hooks/settings/useCompanyTimezone';
import { dateFilterToApiRange, previousDashboardApiRange } from './dashboardRange';

export type DatePeriod = 'all_time' | 'today' | 'this_week' | 'this_month' | 'custom';

export interface DateFilter {
  period: DatePeriod;
  startDate?: string; // YYYY-MM-DD
  endDate?: string; // YYYY-MM-DD
}

export function buildFilterPayload(filter: DateFilter) {
  if (filter.period === 'custom' && filter.startDate && filter.endDate) {
    return { overrideFilters: { startDate: filter.startDate, endDate: filter.endDate } };
  }
  if (filter.period === 'all_time') {
    return { overrideFilters: {} };
  }
  return { overrideFilters: { dateFilter: filter.period } };
}

export interface KpiStat {
  count?: number;
  amount?: number;
  totalAmount?: number;
  delta: number | null;
}

export interface DashboardStats {
  jobsToday: KpiStat;
  jobsThisWeek: KpiStat;
  openInvoices: KpiStat & { count: number; totalAmount: number };
  revenueThisMonth: KpiStat & { amount: number };
  newLeads: KpiStat & { count: number };
}

const EMPTY_KPI: KpiStat = { count: 0, amount: 0, totalAmount: 0, delta: null };
const EMPTY_STATS: DashboardStats = {
  jobsToday: EMPTY_KPI,
  jobsThisWeek: EMPTY_KPI,
  openInvoices: { ...EMPTY_KPI, count: 0, totalAmount: 0 },
  revenueThisMonth: { ...EMPTY_KPI, amount: 0 },
  newLeads: { ...EMPTY_KPI, count: 0 },
};

export async function fetchDashboardStats(
  filter: DateFilter,
  referenceDate = new Date(),
  timeZone?: string,
): Promise<DashboardStats> {
  const currentRange = dateFilterToApiRange(filter, referenceDate, timeZone);
  const previousRange = previousDashboardApiRange(filter, referenceDate, timeZone);
  const [current, previous] = await Promise.all([
    apiGet<JobListResponse>(operationsClient, jobEndpoints.list(), {
      params: { ...currentRange, page: 1, limit: 1 },
    }),
    apiGet<JobListResponse>(operationsClient, jobEndpoints.list(), {
      params: { ...previousRange, page: 1, limit: 1 },
    }),
  ]);
  const count = Number(current.total ?? 0);
  const previousCount = Number(previous.total ?? 0);
  const jobs: KpiStat = {
    count,
    delta: previousCount > 0 ? ((count - previousCount) / previousCount) * 100 : null,
  };
  return { ...EMPTY_STATS, jobsToday: jobs, jobsThisWeek: jobs };
}

export function useDashboardStats(filter: DateFilter = { period: 'this_week' }) {
  const canViewJobs = useHasPermission('jobs:view');
  const timeZone = useCompanyTimezone();
  const cacheKey =
    filter.period === 'custom' ? `${filter.startDate}_${filter.endDate}` : filter.period;
  return useApiQuery<DashboardStats>({
    // Nest beneath the regular jobs-list prefix so create/update mutations
    // invalidate this count together with the Jobs page.
    queryKey: [...jobKeys.lists(), 'dashboard-count', cacheKey, timeZone],
    queryFn: () => fetchDashboardStats(filter, new Date(), timeZone),
    enabled: canViewJobs,
    showErrorToast: false,
  });
}
